import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Archive, RotateCcw } from 'lucide-react';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { SessionExpiredModal, SessionWarningBanner } from '../components/SessionExpiredModal';
import './SuperAdminDashboard.css';


import { API } from '../config';

export default function SuperAdminDashboard({ user }) {
  const { settings: platformSettings } = usePlatformSettings();
  const { sessionExpired, showWarning, handleSessionLogout, dismissWarning } = useInactivityTimeout();
  const [activeTab, setActiveTab] = useState('overview');
  const [groups, setGroups] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get permissions for limited admins
  const permissions = user?.permissions || {};
  const isLimitedAdmin = user?.role === 'limited_admin';
  
  // Helper function to check if user has access to a resource
  const hasAccess = (resource, requiredLevel = 'read') => {
    if (!isLimitedAdmin) return true; // Super admin has full access
    const userLevel = permissions[resource] || 'none';
    if (userLevel === 'none') return false;
    if (requiredLevel === 'read') return userLevel === 'read' || userLevel === 'write';
    if (requiredLevel === 'write') return userLevel === 'write';
    return false;
  };

  // Set initial tab to first accessible tab for limited admins
  useEffect(() => {
    if (isLimitedAdmin) {
      const tabs = ['dashboard', 'groups', 'leadership', 'analytics', 'revenue', 'settings', 'support', 'subscriptions'];
      const firstAccessible = tabs.find(tab => hasAccess(tab === 'dashboard' ? 'dashboard' : tab));
      if (firstAccessible) {
        setActiveTab(firstAccessible === 'dashboard' ? 'overview' : firstAccessible);
      }
    }
  }, [isLimitedAdmin, permissions]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [groupsRes, leadershipRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/groups`),
        axios.get(`${API}/admin/leadership`),
        axios.get(`${API}/admin/stats`)
      ]);

      setGroups(groupsRes.data);
      setLeadership(leadershipRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const approveLeadership = async (userId) => {
    try {
      await axios.post(`${API}/admin/leadership/${userId}/approve`);
      toast.success('Leadership approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve leadership');
    }
  };

  const rejectLeadership = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this leadership application?')) {
      return;
    }

    try {
      await axios.post(`${API}/admin/leadership/${userId}/reject`);
      toast.success('Leadership rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject leadership');
    }
  };

  const [deleteModal, setDeleteModal] = useState({ open: false, groupId: null, groupName: '' });
  const [deleteForm, setDeleteForm] = useState({ confirmName: '', password: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const openDeleteModal = (groupId, groupName) => {
    setDeleteModal({ open: true, groupId, groupName });
    setDeleteForm({ confirmName: '', password: '' });
  };

  const executeDelete = async () => {
    if (deleteForm.confirmName !== deleteModal.groupName) {
      toast.error('Group name does not match.');
      return;
    }
    if (!deleteForm.password) {
      toast.error('Please enter your admin password.');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/groups/${deleteModal.groupId}`, {
        data: { confirm_group_name: deleteForm.confirmName, admin_password: deleteForm.password }
      });
      toast.success('Group archived. It can be restored within 90 days.');
      setDeleteModal({ open: false, groupId: null, groupName: '' });
      loadData();
      loadDeletedGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive group');
    } finally {
      setDeleteLoading(false);
    }
  };

  const [deletedGroups, setDeletedGroups] = useState([]);
  const loadDeletedGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/groups/deleted`);
      setDeletedGroups(res.data);
    } catch (e) { /* no deleted groups */ }
  };

  const restoreGroup = async (groupId) => {
    try {
      await axios.post(`${API}/admin/groups/${groupId}/restore`);
      toast.success('Group restored successfully!');
      loadData();
      loadDeletedGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore group');
    }
  };

  useEffect(() => { loadDeletedGroups(); }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard" data-testid="super-admin-dashboard">
      {sessionExpired && <SessionExpiredModal onLogin={handleSessionLogout} />}
      {showWarning && !sessionExpired && <SessionWarningBanner onDismiss={dismissWarning} />}
      <nav className="dashboard-nav">
        <div className="nav-left">
          <div className="logo">
            {platformSettings.platform_logo_url ? (
              <img 
                src={platformSettings.platform_logo_url} 
                alt="Logo" 
                className="logo-image"
              />
            ) : (
              <span className="logo-icon">{platformSettings.platform_logo_text || '💰'}</span>
            )}
            <span className="logo-text">{platformSettings.platform_name || 'ChamaLink'}</span>
            <span className="admin-badge">ADMIN</span>
          </div>
        </div>
        <div className="nav-right">
          <span className="user-name">👤 {user.name}</span>
          <button onClick={handleLogout} className="btn-logout" data-testid="logout-btn">Logout</button>
        </div>
      </nav>

      <div className="admin-container">
        <aside className="sidebar">
          <div className="sidebar-section">
            <h3>Admin Panel</h3>
            {isLimitedAdmin && (
              <div style={{ padding: '8px 12px', background: '#FEF3C7', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', color: '#92400E' }}>
                ⚠️ Limited Access Account
              </div>
            )}
            <div className="nav-menu">
              {hasAccess('dashboard') && (
                <button
                  className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                  data-testid="tab-overview"
                >
                  📊 Overview
                </button>
              )}
              {hasAccess('groups') && (
                <button
                  className={`nav-item ${activeTab === 'groups' ? 'active' : ''}`}
                  onClick={() => setActiveTab('groups')}
                  data-testid="tab-groups"
                >
                  🏢 All Groups
                </button>
              )}
              {hasAccess('leadership') && (
                <button
                  className={`nav-item ${activeTab === 'leadership' ? 'active' : ''}`}
                  onClick={() => setActiveTab('leadership')}
                  data-testid="tab-leadership"
                >
                  👥 Leadership
                </button>
              )}
              {hasAccess('analytics') && (
                <button
                  className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analytics')}
                  data-testid="tab-analytics"
                >
                  📊 Analytics
                </button>
              )}
              {hasAccess('revenue') && (
                <button
                  className={`nav-item ${activeTab === 'revenue' ? 'active' : ''}`}
                  onClick={() => setActiveTab('revenue')}
                  data-testid="tab-revenue"
                >
                  💰 Revenue
                </button>
              )}
              {hasAccess('settings') && (
                <button
                  className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                  data-testid="tab-settings"
                >
                  ⚙️ Settings
                </button>
              )}
              {hasAccess('support') && (
                <button
                  className={`nav-item ${activeTab === 'support' ? 'active' : ''}`}
                  onClick={() => setActiveTab('support')}
                  data-testid="tab-support"
                >
                  🎫 Support
                </button>
              )}
              {hasAccess('subscriptions') && (
                <button
                  className={`nav-item ${activeTab === 'subscriptions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('subscriptions')}
                  data-testid="tab-subscriptions"
                >
                  💳 Subscriptions
                </button>
              )}
              {!isLimitedAdmin && (
                <button
                  className={`nav-item ${activeTab === 'admin-access' ? 'active' : ''}`}
                  onClick={() => setActiveTab('admin-access')}
                  data-testid="tab-admin-access"
                >
                  🔐 Admin Access
                </button>
              )}
              {!isLimitedAdmin && (
                <button
                  className={`nav-item ${activeTab === 'login-activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('login-activity')}
                  data-testid="tab-login-activity"
                >
                  🔍 Login Activity
                </button>
              )}
            </div>
          </div>
        </aside>

        <main className="main-content">
          {activeTab === 'overview' && hasAccess('dashboard') && <OverviewTab stats={stats} />}
          {activeTab === 'analytics' && hasAccess('analytics') && <AdminAnalyticsTab />}
          {activeTab === 'revenue' && hasAccess('revenue') && <RevenueTab />}
          {activeTab === 'settings' && hasAccess('settings') && <SettingsTab readOnly={!hasAccess('settings', 'write')} />}
          {activeTab === 'support' && hasAccess('support') && <AdminSupportTab />}
          {activeTab === 'subscriptions' && hasAccess('subscriptions') && <SubscriptionsTab readOnly={!hasAccess('subscriptions', 'write')} />}
          {activeTab === 'admin-access' && !isLimitedAdmin && <AdminAccessTab />}
          {activeTab === 'login-activity' && !isLimitedAdmin && <LoginActivityTab />}
          {activeTab === 'groups' && hasAccess('groups') && (
            <GroupsTab groups={groups} onDelete={hasAccess('groups', 'write') ? openDeleteModal : null} deletedGroups={deletedGroups} onRestore={hasAccess('groups', 'write') ? restoreGroup : null} />
          )}
          {activeTab === 'leadership' && hasAccess('leadership') && (
            <LeadershipTab 
              leadership={leadership} 
              onApprove={approveLeadership}
              onReject={rejectLeadership}
            />
          )}
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div data-testid="delete-group-modal" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '460px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', color: '#991B1B', fontWeight: '700' }}>
              Archive Group
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6B7280', lineHeight: '1.6' }}>
              This will archive <strong>"{deleteModal.groupName}"</strong> and all its data. 
              The group can be restored within <strong>90 days</strong> before it is permanently purged.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Type the group name to confirm
              </label>
              <input
                type="text"
                data-testid="delete-confirm-name-input"
                value={deleteForm.confirmName}
                onChange={(e) => setDeleteForm(f => ({ ...f, confirmName: e.target.value }))}
                placeholder={deleteModal.groupName}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '8px',
                  border: `2px solid ${deleteForm.confirmName === deleteModal.groupName ? '#059669' : '#D1D5DB'}`,
                  fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Enter your admin password
              </label>
              <input
                type="password"
                data-testid="delete-confirm-password-input"
                value={deleteForm.password}
                onChange={(e) => setDeleteForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Your admin password"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '8px',
                  border: '2px solid #D1D5DB', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                data-testid="delete-cancel-btn"
                onClick={() => setDeleteModal({ open: false, groupId: null, groupName: '' })}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: '1px solid #D1D5DB',
                  background: '#fff', color: '#374151', fontWeight: '600', fontSize: '14px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={executeDelete}
                disabled={deleteLoading || deleteForm.confirmName !== deleteModal.groupName || !deleteForm.password}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: deleteForm.confirmName === deleteModal.groupName && deleteForm.password ? '#DC2626' : '#E5E7EB',
                  color: deleteForm.confirmName === deleteModal.groupName && deleteForm.password ? '#fff' : '#9CA3AF',
                  fontWeight: '700', fontSize: '14px', cursor: 'pointer',
                  opacity: deleteLoading ? 0.6 : 1
                }}
              >
                {deleteLoading ? 'Archiving...' : 'Archive Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab({ stats }) {
  if (!stats) return <div>Loading...</div>;

  return (
    <div className="overview-tab" data-testid="overview-tab">
      <h1>Platform Overview</h1>
      
      <div className="stats-grid">
        <div className="stat-card card">
          <div className="stat-icon">🏢</div>
          <div className="stat-value">{stats.total_groups}</div>
          <div className="stat-label">Total Groups</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{stats.total_members}</div>
          <div className="stat-label">Total Members</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{stats.total_leadership}</div>
          <div className="stat-label">Approved Leadership</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">⏳</div>
          <div className="stat-value">{stats.pending_leadership}</div>
          <div className="stat-label">Pending Approvals</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">💵</div>
          <div className="stat-value">${stats.total_amount.toFixed(2)}</div>
          <div className="stat-label">Total Collected</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">📝</div>
          <div className="stat-value">{stats.total_payments}</div>
          <div className="stat-label">Total Payments</div>
        </div>
      </div>

      <div className="info-section card mt-3">
        <h2>Platform Health</h2>
        <div className="health-grid">
          <div className="health-item">
            <span className="health-label">Average Members per Group:</span>
            <span className="health-value">
              {stats.total_groups > 0 ? (stats.total_members / stats.total_groups).toFixed(1) : 0}
            </span>
          </div>
          <div className="health-item">
            <span className="health-label">Average Payments per Group:</span>
            <span className="health-value">
              {stats.total_groups > 0 ? (stats.total_payments / stats.total_groups).toFixed(1) : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupsTab({ groups, onDelete, deletedGroups = [], onRestore }) {
  return (
    <div className="groups-tab" data-testid="groups-tab">
      <div className="tab-header">
        <h1>All Groups on Platform</h1>
      </div>

      {groups.length === 0 ? (
        <div className="empty-state card">
          <h3>No groups yet</h3>
          <p>Groups will appear here once leadership creates them</p>
        </div>
      ) : (
        <div className="groups-grid">
          {groups.map((group) => (
            <div key={group.id} className="group-card card" data-testid={`group-card-${group.id}`}>
              <div className="group-header">
                <h3>{group.name}</h3>
                {onDelete && (
                  <button 
                    onClick={() => onDelete(group.id, group.name)}
                    className="btn-delete"
                    data-testid={`delete-group-${group.id}`}
                    title="Archive group"
                    style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}
                  >
                    <Archive size={14} /> Archive
                  </button>
                )}
              </div>
              <p className="group-description">{group.description}</p>
              <div className="group-stats">
                <div className="stat-row">
                  <span>Subscription:</span>
                  <strong className={`status-badge status-${group.subscription_status || 'trial'}`}>
                    {group.subscription_status || 'trial'}
                  </strong>
                </div>
                <div className="stat-row">
                  <span>Tier:</span>
                  <strong>{group.subscription_tier || 'tier1'}</strong>
                </div>
                <div className="stat-row">
                  <span>Members:</span>
                  <strong>{group.member_count}</strong>
                </div>
                <div className="stat-row">
                  <span>Payments:</span>
                  <strong>{group.payment_count}</strong>
                </div>
                <div className="stat-row">
                  <span>Collected:</span>
                  <strong>{(group.currency === 'KES' ? 'KSh' : group.currency === 'EUR' ? '€' : group.currency === 'GBP' ? '£' : group.currency === 'NGN' ? '₦' : group.currency === 'ZAR' ? 'R' : '$')}{group.total_collected.toFixed(2)}</strong>
                </div>
                <div className="stat-row">
                  <span>Frequency:</span>
                  <strong>{group.payment_frequency}</strong>
                </div>
                <div className="stat-row">
                  <span>Amount:</span>
                  <strong>{(group.currency === 'KES' ? 'KSh' : group.currency === 'EUR' ? '€' : group.currency === 'GBP' ? '£' : group.currency === 'NGN' ? '₦' : group.currency === 'ZAR' ? 'R' : '$')}{group.payment_amount}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deletedGroups.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#991B1B', marginBottom: '16px' }}>
            Archived Groups ({deletedGroups.length})
          </h2>
          <div className="groups-grid">
            {deletedGroups.map((group) => (
              <div key={group.id} className="group-card card" data-testid={`deleted-group-${group.id}`} style={{ opacity: 0.7, border: '1px dashed #DC2626' }}>
                <div className="group-header">
                  <h3 style={{ color: '#991B1B' }}>{group.name}</h3>
                  {onRestore && (
                    <button 
                      onClick={() => onRestore(group.id)}
                      data-testid={`restore-group-${group.id}`}
                      style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #059669', background: '#ECFDF5', color: '#065F46', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <RotateCcw size={14} /> Restore
                    </button>
                  )}
                </div>
                <p className="group-description">{group.description}</p>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                  Archived: {group.deleted_at ? new Date(group.deleted_at).toLocaleDateString() : 'Unknown'}
                </div>
                {group.purge_after && (
                  <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', fontWeight: '600' }}>
                    Auto-purge: {new Date(group.purge_after).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadershipTab({ leadership, onApprove, onReject }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLeader, setExpandedLeader] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  
  // Leader Elections State
  const [pendingElections, setPendingElections] = useState([]);
  const [loadingElections, setLoadingElections] = useState(true);
  const [processingElection, setProcessingElection] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [electionToReject, setElectionToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Filter leadership based on search term
  const filterLeadership = (list) => {
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(l => 
      l.name?.toLowerCase().includes(term) ||
      l.email?.toLowerCase().includes(term) ||
      l.groups?.some(g => g.name?.toLowerCase().includes(term))
    );
  };

  const pending = filterLeadership(leadership.filter(l => !l.approved));
  const approved = filterLeadership(leadership.filter(l => l.approved));

  // Load pending leader elections
  useEffect(() => {
    loadPendingElections();
  }, []);

  const loadPendingElections = async () => {
    try {
      const response = await axios.get(`${API}/admin/leader-elections`);
      setPendingElections(response.data);
    } catch (error) {
      console.error('Failed to load pending elections:', error);
    } finally {
      setLoadingElections(false);
    }
  };

  const handleApproveElection = async (election) => {
    if (!window.confirm(`Approve ${election.member_name} as a leader of ${election.group_name}?`)) {
      return;
    }

    setProcessingElection(election.id);
    try {
      const response = await axios.put(`${API}/admin/leader-elections/${election.id}/approve`);
      toast.success(`${election.member_name} has been approved as a leader!`);
      
      // Show temp password if a new account was created
      if (response.data.temp_password) {
        alert(`A new leadership account was created for ${election.member_name}.\n\nTemporary Password: ${response.data.temp_password}\n\nPlease share this password securely with the new leader.`);
      }
      
      loadPendingElections();
      window.location.reload(); // Refresh leadership list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve election');
    } finally {
      setProcessingElection(null);
    }
  };

  const openRejectModal = (election) => {
    setElectionToReject(election);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectElection = async () => {
    if (!electionToReject) return;

    setProcessingElection(electionToReject.id);
    try {
      await axios.put(`${API}/admin/leader-elections/${electionToReject.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success(`Leadership nomination for ${electionToReject.member_name} has been rejected`);
      setShowRejectModal(false);
      setElectionToReject(null);
      setRejectReason('');
      loadPendingElections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject election');
    } finally {
      setProcessingElection(null);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/groups`);
      setAvailableGroups(response.data);
    } catch (error) {
      toast.error('Failed to load groups');
    }
  };

  const handleAddToGroup = async (leaderId) => {
    setSelectedLeader(leaderId);
    await loadGroups();
    setShowAddModal(true);
  };

  const confirmAddToGroup = async () => {
    if (!selectedGroup) {
      toast.error('Please select a group');
      return;
    }

    try {
      await axios.post(`${API}/admin/leadership/${selectedLeader}/add-to-group/${selectedGroup}`);
      toast.success('Leader added to group successfully');
      setShowAddModal(false);
      setSelectedGroup('');
      window.location.reload(); // Refresh to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add leader to group');
    }
  };

  const handleRemoveFromGroup = async (leaderId, groupId, groupName) => {
    if (!window.confirm(`Remove this leader from ${groupName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/leadership/${leaderId}/remove-from-group/${groupId}`);
      toast.success('Leader removed from group successfully');
      window.location.reload(); // Refresh to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove leader from group');
    }
  };

  const handleRemoveFromPlatform = async (leaderId, leaderName) => {
    if (!window.confirm(
      `⚠️ WARNING: Remove ${leaderName} completely from the platform?\n\n` +
      `This will:\n` +
      `• Delete their leadership account\n` +
      `• Remove them from all groups they manage\n` +
      `• This action CANNOT be undone\n\n` +
      `Are you absolutely sure?`
    )) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/leadership/${leaderId}/remove-from-platform`);
      toast.success('Leadership user removed from platform successfully');
      window.location.reload(); // Refresh to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove leadership from platform');
    }
  };

  return (
    <div className="leadership-tab" data-testid="leadership-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <h1 style={{ margin: 0 }}>Leadership Management</h1>
        <div style={{ position: 'relative', minWidth: '300px' }}>
          <input
            type="text"
            placeholder="🔍 Search by name, email, or group..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="leadership-search"
            style={{
              width: '100%',
              padding: '12px 16px 12px 16px',
              borderRadius: '10px',
              border: '2px solid #E5E7EB',
              fontSize: '14px',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
            onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#E5E7EB',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#6B7280'
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div style={{ 
          background: '#EFF6FF', 
          padding: '12px 16px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ color: '#1D4ED8' }}>🔍</span>
          <span style={{ color: '#1E40AF', fontSize: '14px' }}>
            Found <strong>{pending.length + approved.length}</strong> results for "<strong>{searchTerm}</strong>"
          </span>
        </div>
      )}

      {/* Pending Leader Elections from Founding Leaders */}
      {pendingElections.length > 0 && (
        <div className="leadership-section" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h2 style={{ margin: 0 }}>👑 Leader Elections ({pendingElections.length})</h2>
            <span style={{
              background: '#8B5CF6',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600'
            }}>From Group Founders</span>
          </div>
          <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '14px' }}>
            These members have been nominated for leadership by their group's founding leader and require your approval.
          </p>
          <div className="leadership-grid">
            {pendingElections.map((election) => (
              <div key={election.id} className="leader-card card" style={{ 
                border: '2px solid #8B5CF6',
                background: 'linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)'
              }} data-testid={`election-${election.id}`}>
                <div className="leader-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '24px' }}>👤</span>
                    <h3 style={{ margin: 0 }}>{election.member_name}</h3>
                  </div>
                  <p style={{ margin: '0 0 8px 0', color: '#4B5563' }}>{election.member_email}</p>
                  <div style={{ 
                    background: '#EDE9FE', 
                    padding: '10px 12px', 
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6B7280' }}>
                      <strong>Group:</strong> {election.group_name}
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                      <strong>Nominated by:</strong> {election.nominated_by_name}
                    </p>
                  </div>
                  <div className="leader-meta">
                    <span className="status-badge" style={{ background: '#8B5CF6', color: 'white' }}>
                      Awaiting Approval
                    </span>
                    <span className="date-badge">
                      {new Date(election.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="leader-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button 
                    onClick={() => handleApproveElection(election)}
                    className="btn btn-primary"
                    disabled={processingElection === election.id}
                    data-testid={`approve-election-${election.id}`}
                    style={{ flex: 1 }}
                  >
                    {processingElection === election.id ? 'Processing...' : '✅ Approve'}
                  </button>
                  <button 
                    onClick={() => openRejectModal(election)}
                    className="btn-delete"
                    disabled={processingElection === election.id}
                    data-testid={`reject-election-${election.id}`}
                    style={{ flex: 1 }}
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="leadership-section">
          <h2>Pending Approvals ({pending.length})</h2>
          <div className="leadership-grid">
            {pending.map((leader) => (
              <div key={leader.id} className="leader-card card pending" data-testid={`leader-pending-${leader.id}`}>
                <div className="leader-info">
                  <h3>{leader.name}</h3>
                  <p>{leader.email}</p>
                  <div className="leader-meta">
                    <span className="status-badge status-pending">Pending Approval</span>
                    <span className="date-badge">
                      Registered: {new Date(leader.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="leader-actions">
                  <button 
                    onClick={() => onApprove(leader.id)}
                    className="btn btn-secondary"
                    data-testid={`approve-${leader.id}`}
                  >
                    ✅ Approve
                  </button>
                  <button 
                    onClick={() => onReject(leader.id)}
                    className="btn-delete"
                    data-testid={`reject-${leader.id}`}
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="leadership-section mt-3">
        <h2>Approved Leadership ({approved.length})</h2>
        {approved.length === 0 ? (
          <div className="empty-state card">
            <p>No approved leadership yet</p>
          </div>
        ) : (
          <div className="approved-leadership-list">
            {approved.map((leader) => (
              <div key={leader.id} className="leader-detail-card card">
                <div className="leader-summary" onClick={() => setExpandedLeader(expandedLeader === leader.id ? null : leader.id)}>
                  <div className="leader-info-row">
                    <div className="leader-basic-info">
                      <h3>{leader.name}</h3>
                      <p className="leader-email">{leader.email}</p>
                    </div>
                    <div className="leader-stats">
                      <span className="group-count-badge">{leader.group_count} {leader.group_count === 1 ? 'Group' : 'Groups'}</span>
                      <span className={`status-badge ${leader.verified ? 'status-approved' : 'status-pending'}`}>
                        {leader.verified ? 'Verified' : 'Unverified'}
                      </span>
                      <span className="expand-icon">{expandedLeader === leader.id ? '▼' : '▶'}</span>
                    </div>
                  </div>
                </div>

                {expandedLeader === leader.id && (
                  <div className="leader-details-expanded">
                    <div className="details-header">
                      <h4>Groups Managed</h4>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAddToGroup(leader.id)}
                      >
                        + Add to Group
                      </button>
                    </div>

                    {leader.groups && leader.groups.length > 0 ? (
                      <div className="groups-managed-list">
                        {leader.groups.map((group) => (
                          <div key={group.id} className="group-managed-item">
                            <div className="group-managed-info">
                              <span className="group-icon">🏢</span>
                              <span className="group-name">{group.name}</span>
                            </div>
                            <button
                              className="btn-remove"
                              onClick={() => handleRemoveFromGroup(leader.id, group.id, group.name)}
                              title="Remove from group"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-groups-message">
                        <p>Not managing any groups yet</p>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleAddToGroup(leader.id)}
                        >
                          Add to First Group
                        </button>
                      </div>
                    )}

                    <div className="leader-meta-info">
                      <p><strong>Joined:</strong> {new Date(leader.created_at).toLocaleDateString()}</p>
                      <p><strong>User ID:</strong> {leader.id}</p>
                    </div>
                    
                    <div className="leader-danger-zone">
                      <h4 className="danger-zone-title">⚠️ Danger Zone</h4>
                      <p className="danger-zone-text">Remove this leadership user completely from the platform. This action cannot be undone.</p>
                      <button
                        className="btn-delete"
                        onClick={() => handleRemoveFromPlatform(leader.id, leader.name)}
                        title="Remove from platform permanently"
                      >
                        🗑️ Remove from Platform
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add to Group Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Leader to Group</h2>
              <button className="close-button" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Select Group *</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="form-control"
                >
                  <option value="">Choose a group...</option>
                  {availableGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={confirmAddToGroup}
                disabled={!selectedGroup}
              >
                Add to Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Election Modal */}
      {showRejectModal && electionToReject && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>❌ Reject Leadership Nomination</h2>
              <button className="close-button" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div style={{ 
                background: '#FEF2F2', 
                padding: '16px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#991B1B' }}>
                  You are about to reject:
                </p>
                <p style={{ margin: '0 0 4px 0', color: '#374151' }}>
                  <strong>Member:</strong> {electionToReject.member_name}
                </p>
                <p style={{ margin: 0, color: '#374151' }}>
                  <strong>Group:</strong> {electionToReject.group_name}
                </p>
              </div>
              
              <div className="form-group">
                <label>Reason for Rejection (Optional)</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejecting this nomination..."
                  rows="3"
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-delete"
                onClick={handleRejectElection}
                disabled={processingElection === electionToReject.id}
                style={{ background: '#EF4444', color: 'white', padding: '10px 20px', borderRadius: '6px' }}
              >
                {processingElection === electionToReject.id ? 'Rejecting...' : 'Reject Nomination'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminAnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const COLORS = ['#27AE60', '#3498DB', '#E74C3C', '#F39C12', '#9B59B6'];

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    if (!analytics) {
      toast.error('No analytics data available');
      return;
    }
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(39, 174, 96);
      doc.text('ChamaLink Platform Analytics', pageWidth / 2, 20, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
      
      // Platform Summary
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Platform Summary', 14, 40);
      
      const summaryData = [
        ['Total Groups', analytics.total_groups || 0],
        ['Active Groups', analytics.active_groups || 0],
        ['Trial Groups', analytics.trial_groups || 0],
        ['Total Members', analytics.total_members || 0],
        ['Active Members', analytics.active_members || 0],
        ['Total Payments', analytics.total_payments || 0],
        ['Total Revenue', `$${(analytics.total_revenue || 0).toFixed(2)}`],
        ['Total Disbursements', analytics.total_disbursements || 0],
        ['Total Disbursed', `$${(analytics.total_disbursed || 0).toFixed(2)}`],
        ['Net Platform Balance', `$${(analytics.net_platform_balance || 0).toFixed(2)}`]
      ];
      
      autoTable(doc, {
        startY: 45,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });
      
      // Top Groups
      if (analytics.top_groups && analytics.top_groups.length > 0) {
        const finalY = (doc.lastAutoTable?.finalY || 100) + 10;
        doc.setFontSize(14);
        doc.text('Top Performing Groups', 14, finalY);
        
        const topGroupsData = analytics.top_groups.map(group => [
          group.group_name || 'Unknown',
          group.member_count || 0,
          `$${(group.revenue || 0).toFixed(2)}`
        ]);
        
        autoTable(doc, {
          startY: finalY + 5,
          head: [['Group Name', 'Members', 'Revenue']],
          body: topGroupsData,
          theme: 'striped',
          headStyles: { fillColor: [39, 174, 96] },
          margin: { left: 14, right: 14 }
        });
      }
      
      // Revenue Trends
      if (analytics.revenue_trends && analytics.revenue_trends.length > 0) {
        const finalY2 = (doc.lastAutoTable?.finalY || 150) + 10;
        doc.setFontSize(14);
        doc.text('Revenue Trends', 14, finalY2);
        
        const trendsData = analytics.revenue_trends.map(trend => [
          trend.month || 'Unknown',
          `$${(trend.revenue || 0).toFixed(2)}`
        ]);
        
        autoTable(doc, {
          startY: finalY2 + 5,
          head: [['Month', 'Revenue']],
          body: trendsData,
          theme: 'striped',
          headStyles: { fillColor: [39, 174, 96] },
          margin: { left: 14, right: 14 }
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount} | ChamaLink Platform Analytics`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      doc.save(`ChamaLink-Platform-Analytics-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  if (loading) {
    return <div>Loading analytics...</div>;
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const groupStatusData = [
    { name: 'Active', value: analytics.active_groups, color: '#27AE60' },
    { name: 'Trial', value: analytics.trial_groups, color: '#3498DB' }
  ];

  const groupSizeData = Object.entries(analytics.group_size_distribution || {}).map(([size, count]) => ({
    name: size,
    count: count
  }));

  return (
    <div className="analytics-tab">
      <div className="analytics-header">
        <h1>📊 Platform Analytics</h1>
        <button className="btn btn-primary" onClick={generatePDF}>
          📥 Download PDF Report
        </button>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card card">
          <div className="metric-icon">🏢</div>
          <div className="metric-content">
            <h3>{analytics.total_groups}</h3>
            <p>Total Groups</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <h3>{analytics.total_members}</h3>
            <p>Total Members</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <h3>${analytics.total_revenue.toFixed(2)}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <h3>${analytics.net_platform_balance.toFixed(2)}</h3>
            <p>Net Balance</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Group Status */}
        <div className="chart-card card">
          <h2>Group Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={groupStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, value}) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {groupStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Group Size Distribution */}
        {groupSizeData.length > 0 && (
          <div className="chart-card card">
            <h2>Group Size Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={groupSizeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#27AE60" name="Number of Groups" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Revenue Trends */}
        {analytics.revenue_trends && analytics.revenue_trends.length > 0 && (
          <div className="chart-card card chart-full-width">
            <h2>Revenue Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.revenue_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#27AE60" strokeWidth={2} name="Revenue ($)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Performing Groups */}
      {analytics.top_groups && analytics.top_groups.length > 0 && (
        <div className="top-groups card">
          <h2>Top Performing Groups</h2>
          <div className="top-groups-grid">
            {analytics.top_groups.map((group, index) => (
              <div key={group.group_id} className="top-group-item">
                <div className="rank">#{index + 1}</div>
                <div className="group-info">
                  <h3>{group.group_name}</h3>
                  <p>{group.member_count} members</p>
                </div>
                <div className="group-revenue">
                  ${group.revenue.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Platform Financial Overview */}
      <div className="financial-overview card">
        <h2>Platform Financial Overview</h2>
        <div className="financial-details">
          <div className="financial-row">
            <span>Total Revenue:</span>
            <strong className="positive">${analytics.total_revenue.toFixed(2)}</strong>
          </div>
          <div className="financial-row">
            <span>Total Disbursed:</span>
            <strong className="negative">-${analytics.total_disbursed.toFixed(2)}</strong>
          </div>
          <div className="financial-row total">
            <span>Net Platform Balance:</span>
            <strong>${analytics.net_platform_balance.toFixed(2)}</strong>
          </div>
          <div className="financial-row">
            <span>Total Payments:</span>
            <strong>{analytics.total_payments}</strong>
          </div>
          <div className="financial-row">
            <span>Total Disbursements:</span>
            <strong>{analytics.total_disbursements}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueTab() {
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const COLORS = ['#27AE60', '#3498DB', '#E74C3C', '#F39C12'];

  useEffect(() => {
    loadRevenue();
  }, []);

  const loadRevenue = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue`);
      setRevenue(response.data);
    } catch (error) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    if (!revenue) {
      toast.error('No revenue data available');
      return;
    }
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(39, 174, 96);
      doc.text('ChamaLink Revenue Report', pageWidth / 2, 20, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
      
      // Revenue Summary
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Revenue Summary', 14, 40);
      
      const summaryData = [
        ['Total Revenue', `$${(revenue.total_revenue || 0).toFixed(2)}`],
        ['Subscription Revenue', `$${(revenue.total_subscription_revenue || 0).toFixed(2)}`],
        ['Platform Fee Revenue', `$${(revenue.total_platform_fees || 0).toFixed(2)}`],
        ['Active Subscriptions', revenue.active_subscriptions || 0],
        ['Platform Fee Transactions', revenue.platform_fee_transactions || 0],
        ['Average Platform Fee', `$${(revenue.average_platform_fee || 0).toFixed(2)}`],
        ['Subscription %', `${(revenue.subscription_percentage || 0).toFixed(1)}%`],
        ['Platform Fee %', `${(revenue.platform_fee_percentage || 0).toFixed(1)}%`]
      ];
      
      autoTable(doc, {
        startY: 45,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });
    
      // Tier Breakdown
      if (revenue.tier_breakdown && revenue.tier_breakdown.length > 0) {
        const finalY = (doc.lastAutoTable?.finalY || 100) + 10;
        doc.setFontSize(14);
        doc.text('Subscription Tier Breakdown', 14, finalY);
        
        const tierData = revenue.tier_breakdown.map(tier => [
          (tier.tier || 'Unknown').toUpperCase(),
          `$${(tier.monthly_price || 0).toFixed(2)}`,
          tier.active_groups || 0,
          `$${(tier.monthly_revenue || 0).toFixed(2)}`
        ]);
        
        autoTable(doc, {
          startY: finalY + 5,
          head: [['Tier', 'Price/Month', 'Active Groups', 'Monthly Revenue']],
          body: tierData,
          theme: 'striped',
          headStyles: { fillColor: [39, 174, 96] },
          margin: { left: 14, right: 14 }
        });
      }
      
      // Revenue Trends
      if (revenue.revenue_trends && revenue.revenue_trends.length > 0) {
        const finalY2 = (doc.lastAutoTable?.finalY || 150) + 10;
        doc.setFontSize(14);
        doc.text('Revenue Trends', 14, finalY2);
        
        const trendsData = revenue.revenue_trends.map(trend => [
          trend.month || 'Unknown',
          `$${(trend.subscription || 0).toFixed(2)}`,
          `$${(trend.platform_fees || 0).toFixed(2)}`,
          `$${(trend.total || 0).toFixed(2)}`
        ]);
        
        autoTable(doc, {
          startY: finalY2 + 5,
          head: [['Month', 'Subscriptions', 'Platform Fees', 'Total']],
          body: trendsData,
          theme: 'striped',
          headStyles: { fillColor: [39, 174, 96] },
          margin: { left: 14, right: 14 }
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount} | ChamaLink Revenue Report`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      doc.save(`ChamaLink-Revenue-Report-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Revenue report downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  if (loading) {
    return <div>Loading revenue data...</div>;
  }

  if (!revenue) {
    return <div>No revenue data available</div>;
  }

  const revenueSourceData = [
    { 
      name: 'Subscriptions', 
      value: revenue.total_subscription_revenue,
      percentage: revenue.subscription_percentage,
      color: '#27AE60'
    },
    { 
      name: 'Platform Fees', 
      value: revenue.total_platform_fees,
      percentage: revenue.platform_fee_percentage,
      color: '#3498DB'
    }
  ];

  return (
    <div className="revenue-tab">
      <div className="revenue-header">
        <h1>💰 Revenue Summary</h1>
        <button className="btn btn-primary" onClick={generatePDF}>
          📥 Download Revenue Report
        </button>
      </div>

      {/* Total Revenue Card */}
      <div className="total-revenue-card card">
        <div className="total-revenue-content">
          <div className="revenue-icon">💵</div>
          <div>
            <h2>Total Platform Revenue</h2>
            <h1 className="revenue-amount">${revenue.total_revenue.toFixed(2)}</h1>
            <p className="revenue-subtitle">Combined subscription & platform fees</p>
          </div>
        </div>
      </div>

      {/* Revenue Sources */}
      <div className="revenue-sources-grid">
        <div className="revenue-source-card card subscription-card">
          <div className="source-header">
            <h3>📊 Subscription Revenue</h3>
            <span className="percentage-badge">{revenue.subscription_percentage.toFixed(1)}%</span>
          </div>
          <div className="source-amount">${revenue.total_subscription_revenue.toFixed(2)}</div>
          <div className="source-details">
            <div className="detail-item">
              <span>Active Subscriptions:</span>
              <strong>{revenue.active_subscriptions}</strong>
            </div>
          </div>
        </div>

        <div className="revenue-source-card card fees-card">
          <div className="source-header">
            <h3>💳 Platform Fee Revenue</h3>
            <span className="percentage-badge">{revenue.platform_fee_percentage.toFixed(1)}%</span>
          </div>
          <div className="source-amount">${revenue.total_platform_fees.toFixed(2)}</div>
          <div className="source-details">
            <div className="detail-item">
              <span>Fee Transactions:</span>
              <strong>{revenue.platform_fee_transactions}</strong>
            </div>
            <div className="detail-item">
              <span>Average Fee:</span>
              <strong>${revenue.average_platform_fee.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="revenue-charts-grid">
        {/* Revenue Source Distribution */}
        <div className="chart-card card">
          <h2>Revenue Source Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueSourceData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, percentage}) => `${name}: ${percentage.toFixed(1)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {revenueSourceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trends */}
        {revenue.revenue_trends && revenue.revenue_trends.length > 0 && (
          <div className="chart-card card chart-full-width">
            <h2>Revenue Trends (Last 6 Months)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenue.revenue_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                <Legend />
                <Line type="monotone" dataKey="subscription" stroke="#27AE60" strokeWidth={2} name="Subscriptions" />
                <Line type="monotone" dataKey="platform_fees" stroke="#3498DB" strokeWidth={2} name="Platform Fees" />
                <Line type="monotone" dataKey="total" stroke="#9B59B6" strokeWidth={3} name="Total Revenue" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Subscription Tier Breakdown */}
      {revenue.tier_breakdown && revenue.tier_breakdown.length > 0 && (
        <div className="tier-breakdown card">
          <h2>Subscription Tier Breakdown</h2>
          <div className="tier-grid">
            {revenue.tier_breakdown.map((tier) => (
              <div key={tier.tier} className="tier-item">
                <div className="tier-header">
                  <h3>{tier.tier.toUpperCase()}</h3>
                  <span className="tier-price">${tier.monthly_price}/mo</span>
                </div>
                <div className="tier-stats">
                  <div className="tier-stat">
                    <span>Active Groups:</span>
                    <strong>{tier.active_groups}</strong>
                  </div>
                  <div className="tier-stat">
                    <span>Monthly Revenue:</span>
                    <strong className="revenue-highlight">${tier.monthly_revenue.toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revenue Insights */}
      <div className="revenue-insights card">
        <h2>💡 Revenue Insights</h2>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div className="insight-content">
              <h4>Primary Revenue Stream</h4>
              <p>{revenue.subscription_percentage > revenue.platform_fee_percentage ? 'Subscriptions' : 'Platform Fees'} account for {Math.max(revenue.subscription_percentage, revenue.platform_fee_percentage).toFixed(1)}% of total revenue</p>
            </div>
          </div>
          
          <div className="insight-item">
            <div className="insight-icon">💵</div>
            <div className="insight-content">
              <h4>Average Transaction Value</h4>
              <p>${revenue.average_platform_fee.toFixed(2)} per platform fee transaction</p>
            </div>
          </div>
          
          <div className="insight-item">
            <div className="insight-icon">🎯</div>
            <div className="insight-content">
              <h4>Revenue Diversification</h4>
              <p>{revenue.subscription_percentage.toFixed(0)}% subscription / {revenue.platform_fee_percentage.toFixed(0)}% platform fees</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminAccessTab() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    permissions: {
      dashboard: 'none',
      groups: 'none',
      leadership: 'none',
      subscriptions: 'none',
      support: 'none',
      analytics: 'none',
      revenue: 'none',
      settings: 'none'
    }
  });

  const PERMISSION_OPTIONS = [
    { value: 'none', label: 'No Access', color: '#6B7280' },
    { value: 'read', label: 'Read Only', color: '#3B82F6' },
    { value: 'write', label: 'Read & Write', color: '#10B981' }
  ];

  const RESOURCES = [
    { key: 'dashboard', label: 'Dashboard Overview', icon: '📊' },
    { key: 'groups', label: 'Groups Management', icon: '👥' },
    { key: 'leadership', label: 'Leadership Management', icon: '👔' },
    { key: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { key: 'support', label: 'Support Tickets', icon: '🎫' },
    { key: 'analytics', label: 'Analytics', icon: '📈' },
    { key: 'revenue', label: 'Revenue Reports', icon: '💰' },
    { key: 'settings', label: 'Platform Settings', icon: '⚙️' }
  ];

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/limited-admins`);
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load admin accounts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      permissions: {
        dashboard: 'none',
        groups: 'none',
        leadership: 'none',
        subscriptions: 'none',
        support: 'none',
        analytics: 'none',
        revenue: 'none',
        settings: 'none'
      }
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/admin/limited-admins`, formData);
      toast.success('Limited admin account created successfully');
      setShowCreateModal(false);
      resetForm();
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setProcessing(false);
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      email: admin.email,
      name: admin.name,
      password: '',
      permissions: admin.permissions || {
        dashboard: 'none',
        groups: 'none',
        leadership: 'none',
        subscriptions: 'none',
        support: 'none',
        analytics: 'none',
        revenue: 'none',
        settings: 'none'
      }
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/limited-admins/${selectedAdmin.id}`, {
        name: formData.name,
        permissions: formData.permissions
      });
      toast.success('Admin account updated successfully');
      setShowEditModal(false);
      setSelectedAdmin(null);
      resetForm();
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin account');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleActive = async (admin) => {
    const newStatus = !admin.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} admin account for ${admin.name}?`)) return;
    
    try {
      await axios.put(`${API}/admin/limited-admins/${admin.id}`, {
        is_active: newStatus
      });
      toast.success(`Admin account ${action}d`);
      loadAdmins();
    } catch (error) {
      toast.error(`Failed to ${action} admin account`);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin account for ${admin.name}? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/limited-admins/${admin.id}`);
      toast.success('Admin account deleted');
      loadAdmins();
    } catch (error) {
      toast.error('Failed to delete admin account');
    }
  };

  const handleResetPassword = async (admin) => {
    if (!window.confirm(`Reset password for ${admin.name}? A temporary password will be generated.`)) return;
    
    try {
      const response = await axios.put(`${API}/admin/limited-admins/${admin.id}/reset-password`);
      toast.success(`Password reset! Temporary password: ${response.data.temporary_password}`);
      // Also show in alert so user can copy it
      alert(`Temporary Password for ${admin.name}:\n\n${response.data.temporary_password}\n\nPlease share this securely with the admin.`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const updatePermission = (resource, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [resource]: value
      }
    }));
  };

  const setAllPermissions = (value) => {
    const newPermissions = {};
    RESOURCES.forEach(r => {
      newPermissions[r.key] = value;
    });
    setFormData(prev => ({
      ...prev,
      permissions: newPermissions
    }));
  };

  const getPermissionBadge = (level) => {
    const option = PERMISSION_OPTIONS.find(o => o.value === level);
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        background: option?.color || '#6B7280',
        color: 'white'
      }}>
        {option?.label || 'No Access'}
      </span>
    );
  };

  if (loading) {
    return <div>Loading admin accounts...</div>;
  }

  return (
    <div className="admin-access-tab" data-testid="admin-access-tab">
      <div className="tab-header">
        <div>
          <h1>🔐 Admin Access Control</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Manage limited admin accounts with granular permissions</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          data-testid="create-admin-btn"
        >
          + Create Limited Admin
        </button>
      </div>

      {/* Stats */}
      <div className="admin-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#3B82F6' }}>{admins.length}</div>
          <div style={{ color: '#64748b' }}>Total Limited Admins</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981' }}>{admins.filter(a => a.is_active !== false).length}</div>
          <div style={{ color: '#64748b' }}>Active</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#EF4444' }}>{admins.filter(a => a.is_active === false).length}</div>
          <div style={{ color: '#64748b' }}>Deactivated</div>
        </div>
      </div>

      {/* Admin List */}
      {admins.length === 0 ? (
        <div className="empty-state card">
          <h3>No limited admin accounts</h3>
          <p>Create a limited admin account to grant restricted access to the admin dashboard</p>
        </div>
      ) : (
        <div className="admin-list">
          {admins.map((admin) => (
            <div key={admin.id} className="card" style={{ marginBottom: '16px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>{admin.name}</h3>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: admin.is_active !== false ? '#10B981' : '#EF4444',
                      color: 'white'
                    }}>
                      {admin.is_active !== false ? 'Active' : 'Deactivated'}
                    </span>
                  </div>
                  <p style={{ color: '#64748b', margin: '0 0 12px 0' }}>{admin.email}</p>
                  
                  {/* Permissions Grid */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {RESOURCES.map(resource => (
                      <div key={resource.key} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '4px 10px',
                        background: '#F8FAFC',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}>
                        <span>{resource.icon}</span>
                        <span style={{ color: '#374151' }}>{resource.label}:</span>
                        {getPermissionBadge(admin.permissions?.[resource.key] || 'none')}
                      </div>
                    ))}
                  </div>
                  
                  <p style={{ color: '#94A3B8', fontSize: '12px', marginTop: '12px' }}>
                    Created by {admin.created_by_name || 'Super Admin'} • {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={() => openEditModal(admin)}
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={() => handleResetPassword(admin)}
                  >
                    🔑 Reset Password
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    style={{ 
                      padding: '6px 12px', 
                      fontSize: '12px',
                      background: admin.is_active !== false ? '#F59E0B' : '#10B981',
                      color: 'white'
                    }}
                    onClick={() => handleToggleActive(admin)}
                  >
                    {admin.is_active !== false ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '12px', background: '#EF4444', color: 'white' }}
                    onClick={() => handleDelete(admin)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>🔐 Create Limited Admin</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreate} style={{ padding: '20px' }}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter admin's full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password * (min 8 characters)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter secure password"
                  minLength={8}
                  required
                />
              </div>

              <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #E2E8F0' }} />

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0 }}>Access Permissions</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => setAllPermissions('none')}>
                      None
                    </button>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', background: '#3B82F6', color: 'white' }} onClick={() => setAllPermissions('read')}>
                      All Read
                    </button>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', background: '#10B981', color: 'white' }} onClick={() => setAllPermissions('write')}>
                      All Write
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {RESOURCES.map(resource => (
                    <div key={resource.key} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '12px',
                      background: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{resource.icon}</span>
                        <span style={{ fontWeight: '500' }}>{resource.label}</span>
                      </div>
                      <select
                        value={formData.permissions[resource.key]}
                        onChange={(e) => updatePermission(resource.key, e.target.value)}
                        style={{ 
                          padding: '6px 12px', 
                          borderRadius: '6px', 
                          border: '1px solid #E2E8F0',
                          background: 'white',
                          fontWeight: '500'
                        }}
                      >
                        {PERMISSION_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={processing}>
                  {processing ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAdmin && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>✏️ Edit Admin Permissions</h2>
              <button className="close-button" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleUpdate} style={{ padding: '20px' }}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter admin's full name"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  style={{ background: '#F1F5F9', cursor: 'not-allowed' }}
                />
                <small style={{ color: '#64748b' }}>Email cannot be changed</small>
              </div>

              <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #E2E8F0' }} />

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0 }}>Access Permissions</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => setAllPermissions('none')}>
                      None
                    </button>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', background: '#3B82F6', color: 'white' }} onClick={() => setAllPermissions('read')}>
                      All Read
                    </button>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', background: '#10B981', color: 'white' }} onClick={() => setAllPermissions('write')}>
                      All Write
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {RESOURCES.map(resource => (
                    <div key={resource.key} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '12px',
                      background: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{resource.icon}</span>
                        <span style={{ fontWeight: '500' }}>{resource.label}</span>
                      </div>
                      <select
                        value={formData.permissions[resource.key]}
                        onChange={(e) => updatePermission(resource.key, e.target.value)}
                        style={{ 
                          padding: '6px 12px', 
                          borderRadius: '6px', 
                          border: '1px solid #E2E8F0',
                          background: 'white',
                          fontWeight: '500'
                        }}
                      >
                        {PERMISSION_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={processing}>
                  {processing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionsTab() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, trial, expired, delinquent
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showCustomPricingModal, setShowCustomPricingModal] = useState(false);
  const [customPricingForm, setCustomPricingForm] = useState({ monthly_price: '', annual_price: '', max_members: '', notes: '' });
  const [newTier, setNewTier] = useState('');
  const [processing, setProcessing] = useState(false);

  const TIERS = {
    tier1: { name: '0-250 Members', maxMembers: 250, monthlyPrice: 29.99 },
    tier2: { name: '251-500 Members', maxMembers: 500, monthlyPrice: 39.99 },
    tier3: { name: '501-750 Members', maxMembers: 750, monthlyPrice: 49.99 },
    tier4: { name: '751-1000 Members', maxMembers: 1000, monthlyPrice: 59.99 },
    tier5: { name: '1001-1500 Members', maxMembers: 1500, monthlyPrice: 69.99 },
    custom: { name: '1500+ Members', maxMembers: 999999, monthlyPrice: null }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/subscriptions/stats`)
      ]);
      setSubscriptions(subsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTier = async () => {
    if (!newTier || !selectedSubscription) return;
    
    if (newTier === 'custom') {
      setShowTierModal(false);
      setCustomPricingForm({ monthly_price: '', annual_price: '', max_members: '', notes: '' });
      setShowCustomPricingModal(true);
      return;
    }

    setProcessing(true);
    try {
      await axios.put(`${API}/admin/subscriptions/${selectedSubscription.group_id}/change-tier?new_tier=${newTier}`);
      toast.success(`Tier changed to ${TIERS[newTier]?.name || newTier}`);
      setShowTierModal(false);
      setSelectedSubscription(null);
      setNewTier('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change tier');
    } finally {
      setProcessing(false);
    }
  };

  const handleSetCustomPricing = async () => {
    if (!selectedSubscription || !customPricingForm.monthly_price) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/subscriptions/${selectedSubscription.group_id}/custom-pricing`, {
        monthly_price: parseFloat(customPricingForm.monthly_price),
        annual_price: customPricingForm.annual_price ? parseFloat(customPricingForm.annual_price) : null,
        max_members: customPricingForm.max_members ? parseInt(customPricingForm.max_members) : null,
        notes: customPricingForm.notes || ''
      });
      toast.success(`Custom pricing set: $${customPricingForm.monthly_price}/month`);
      setShowCustomPricingModal(false);
      setSelectedSubscription(null);
      setCustomPricingForm({ monthly_price: '', annual_price: '', max_members: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set custom pricing');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendPaymentRequest = (subscription) => {
    // Build mailto link with leadership emails
    const leadershipEmails = subscription.leadership_emails || [];
    const groupName = subscription.group_name;
    const tier = subscription.subscription_tier.toUpperCase();
    const tierName = TIERS[subscription.subscription_tier]?.name || '';
    const amount = subscription.monthly_price;
    const daysOverdue = subscription.days_overdue || 0;
    
    const subject = encodeURIComponent(`Payment Required: ${groupName} Subscription Reactivation`);
    
    const body = encodeURIComponent(
`Dear Leadership,

Your group "${groupName}" subscription has expired and requires payment to continue using ChamaLink services.

SUBSCRIPTION DETAILS:
- Group: ${groupName}
- Plan: ${tier} - ${tierName}
- Billing: ${subscription.billing_cycle}
- Amount Due: $${amount.toFixed(2)}/month
${daysOverdue > 0 ? `- Days Overdue: ${daysOverdue} days` : ''}

To reactivate your subscription and regain full access to all features, please log in to your account and complete the payment.

If you have any questions, please contact our support team.

Best regards,
ChamaLink Administration`
    );
    
    const toEmails = leadershipEmails.join(',');
    const mailtoLink = `mailto:${toEmails}?subject=${subject}&body=${body}`;
    
    window.open(mailtoLink, '_blank');
  };

  const handleReactivate = async (subscription) => {
    const days = window.prompt('Enter number of days to extend subscription:', '30');
    if (!days) return;
    
    const daysNum = parseInt(days);
    if (isNaN(daysNum) || daysNum < 1) {
      toast.error('Please enter a valid number of days');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/subscriptions/${subscription.group_id}/reactivate?extend_days=${daysNum}`);
      toast.success(`Subscription reactivated for ${daysNum} days`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspend = async (subscription) => {
    if (!window.confirm(`Suspend subscription for "${subscription.group_name}"? This will mark them as expired.`)) return;
    
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/subscriptions/${subscription.group_id}/suspend`);
      toast.success('Subscription suspended');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleExtendTrial = async (subscription) => {
    const days = window.prompt('Enter number of days to extend trial:', '14');
    if (!days) return;
    
    const daysNum = parseInt(days);
    if (isNaN(daysNum) || daysNum < 1) {
      toast.error('Please enter a valid number of days');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/subscriptions/${subscription.group_id}/extend-trial?extend_days=${daysNum}`);
      toast.success(`Trial extended by ${daysNum} days`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setProcessing(false);
    }
  };

  const openTierModal = (subscription) => {
    setSelectedSubscription(subscription);
    setNewTier(subscription.subscription_tier);
    setShowTierModal(true);
  };

  const getStatusBadgeClass = (status, isDelinquent) => {
    if (isDelinquent) return 'status-delinquent';
    switch (status) {
      case 'active': return 'status-approved';
      case 'trial': return 'status-pending';
      case 'expired': return 'status-inactive';
      case 'cancelled': return 'status-inactive';
      default: return 'status-pending';
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'delinquent') return sub.is_delinquent;
    if (filter === 'active') return sub.subscription_status === 'active' && !sub.is_delinquent;
    if (filter === 'trial') return sub.subscription_status === 'trial' && !sub.is_delinquent;
    if (filter === 'expired') return sub.subscription_status === 'expired' || sub.subscription_status === 'cancelled';
    return true;
  });

  if (loading) {
    return <div>Loading subscription data...</div>;
  }

  return (
    <div className="subscriptions-tab" data-testid="subscriptions-tab">
      <div className="tab-header">
        <h1>💳 Group Subscriptions</h1>
        <p style={{ color: '#64748b', marginTop: '4px' }}>Manage and monitor all group subscriptions</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="subscription-stats-grid">
          <div className="stat-card card" onClick={() => setFilter('all')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">📋</div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Subscriptions</div>
          </div>
          <div className="stat-card card" onClick={() => setFilter('active')} style={{ cursor: 'pointer', borderLeft: '4px solid #27AE60' }}>
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card card" onClick={() => setFilter('trial')} style={{ cursor: 'pointer', borderLeft: '4px solid #3498DB' }}>
            <div className="stat-icon">⏳</div>
            <div className="stat-value">{stats.trial}</div>
            <div className="stat-label">Trial</div>
          </div>
          <div className="stat-card card" onClick={() => setFilter('delinquent')} style={{ cursor: 'pointer', borderLeft: '4px solid #E74C3C' }}>
            <div className="stat-icon">⚠️</div>
            <div className="stat-value">{stats.delinquent}</div>
            <div className="stat-label">Delinquent</div>
          </div>
          <div className="stat-card card" onClick={() => setFilter('expired')} style={{ cursor: 'pointer', borderLeft: '4px solid #6B7280' }}>
            <div className="stat-icon">❌</div>
            <div className="stat-value">{stats.expired + stats.cancelled}</div>
            <div className="stat-label">Expired/Cancelled</div>
          </div>
        </div>
      )}

      {/* MRR Cards */}
      {stats && (
        <div className="mrr-cards" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '20px', background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)', color: 'white' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Monthly Recurring Revenue</h3>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>${stats.total_mrr.toFixed(2)}</div>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.8 }}>From {stats.active} active subscriptions</p>
          </div>
          <div className="card" style={{ padding: '20px', background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: 'white' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Potential Recovery</h3>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>${stats.potential_mrr.toFixed(2)}</div>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.8 }}>If {stats.delinquent} delinquent groups reactivate</p>
          </div>
        </div>
      )}

      {/* Tier Breakdown */}
      {stats && (
        <div className="card" style={{ padding: '20px', marginBottom: '24px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '16px' }}>📊 Subscriptions by Tier</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
            {Object.entries(TIERS).map(([tier, info]) => (
              <div 
                key={tier} 
                className="tier-breakdown-item"
                style={{ 
                  padding: '16px', 
                  background: tier === 'custom' ? '#EDE9FE' : '#F8FAFC', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  border: `1px solid ${tier === 'custom' ? '#C4B5FD' : '#E2E8F0'}`
                }}
              >
                <div style={{ fontSize: '24px', fontWeight: '700', color: tier === 'custom' ? '#7C3AED' : '#27AE60' }}>
                  {stats.by_tier[tier] || 0}
                </div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginTop: '4px' }}>
                  {tier.toUpperCase()}
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                  {info.name}
                </div>
                <div style={{ fontSize: '11px', color: tier === 'custom' ? '#7C3AED' : '#27AE60', marginTop: '4px' }}>
                  {info.monthlyPrice !== null ? `$${info.monthlyPrice}/mo` : 'Admin-set'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar card" style={{ display: 'flex', gap: '8px', padding: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('all')}>
          All ({subscriptions.length})
        </button>
        <button className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('active')}>
          Active ({subscriptions.filter(s => s.subscription_status === 'active' && !s.is_delinquent).length})
        </button>
        <button className={`btn ${filter === 'trial' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('trial')}>
          Trial ({subscriptions.filter(s => s.subscription_status === 'trial' && !s.is_delinquent).length})
        </button>
        <button className={`btn ${filter === 'delinquent' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('delinquent')}>
          ⚠️ Delinquent ({subscriptions.filter(s => s.is_delinquent).length})
        </button>
        <button className={`btn ${filter === 'expired' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('expired')}>
          Expired ({subscriptions.filter(s => s.subscription_status === 'expired' || s.subscription_status === 'cancelled').length})
        </button>
      </div>

      {/* Subscriptions Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Group</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Tier</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Members</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>End Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Leadership</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No subscriptions found for this filter
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map((sub) => (
                  <tr key={sub.group_id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600' }}>{sub.group_name}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{sub.billing_cycle}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600' }}>{sub.subscription_tier.toUpperCase()}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{sub.tier_name}</div>
                      <div style={{ fontSize: '12px', color: '#27AE60' }}>
                        {sub.subscription_tier === 'custom' 
                          ? (sub.custom_pricing ? `$${sub.custom_pricing.monthly_price}/mo` : 'Pricing not set')
                          : `$${sub.monthly_price}/mo`
                        }
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span className={`status-badge ${getStatusBadgeClass(sub.subscription_status, sub.is_delinquent)}`}>
                        {sub.is_delinquent ? 'DELINQUENT' : sub.subscription_status.toUpperCase()}
                      </span>
                      {sub.is_delinquent && (
                        <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>
                          {sub.days_overdue} days overdue
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>{sub.member_count} / {sub.max_members === 999999 ? '∞' : sub.max_members}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {sub.subscription_end_date ? (
                        <div>
                          {new Date(sub.subscription_end_date).toLocaleDateString()}
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {new Date(sub.subscription_end_date).toLocaleTimeString()}
                          </div>
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontSize: '13px' }}>
                        {sub.leadership.slice(0, 2).join(', ')}
                        {sub.leadership.length > 2 && ` +${sub.leadership.length - 2}`}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                          className="btn btn-secondary" 
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          onClick={() => openTierModal(sub)}
                          title="Change Tier"
                        >
                          📊 Tier
                        </button>
                        <button 
                          className="btn btn-secondary" 
                          style={{ padding: '4px 8px', fontSize: '12px', background: '#7C3AED', color: 'white' }}
                          onClick={() => { setSelectedSubscription(sub); setCustomPricingForm({ monthly_price: sub.custom_pricing?.monthly_price || '', annual_price: sub.custom_pricing?.annual_price || '', max_members: sub.custom_pricing?.max_members || '', notes: sub.custom_pricing?.notes || '' }); setShowCustomPricingModal(true); }}
                          title="Set Custom Pricing"
                          data-testid={`custom-pricing-btn-${sub.group_id}`}
                        >
                          💲 Custom
                        </button>
                        {/* Extend Trial - show only for active trial groups */}
                        {sub.subscription_status === 'trial' && !sub.is_delinquent && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ padding: '4px 8px', fontSize: '12px', background: '#3B82F6', color: 'white' }}
                            onClick={() => handleExtendTrial(sub)}
                            disabled={processing}
                            title="Extend Trial Period"
                          >
                            ⏳ Extend Trial
                          </button>
                        )}
                        {(sub.is_delinquent || sub.subscription_status === 'expired') && (
                          <>
                            <button 
                              className="btn btn-primary" 
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => handleSendPaymentRequest(sub)}
                              disabled={processing}
                              title="Send Payment Request"
                            >
                              📧 Request
                            </button>
                            <button 
                              className="btn btn-secondary" 
                              style={{ padding: '4px 8px', fontSize: '12px', background: '#27AE60', color: 'white' }}
                              onClick={() => handleReactivate(sub)}
                              disabled={processing}
                              title="Reactivate Subscription"
                            >
                              ✅ Reactivate
                            </button>
                          </>
                        )}
                        {sub.subscription_status === 'active' && !sub.is_delinquent && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ padding: '4px 8px', fontSize: '12px', background: '#DC2626', color: 'white' }}
                            onClick={() => handleSuspend(sub)}
                            disabled={processing}
                            title="Suspend Subscription"
                          >
                            ⏸️ Suspend
                          </button>
                        )}
                        {sub.currency === 'KES' && sub.group_phone_number && (
                          <button 
                            className="btn btn-secondary" 
                            style={{ padding: '4px 8px', fontSize: '12px', background: '#48BB78', color: 'white' }}
                            onClick={async () => {
                              try {
                                setProcessing(true);
                                const token = localStorage.getItem('token');
                                await axios.post(`${API}/admin/send-subscription-reminder`, 
                                  { group_id: sub.group_id },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success(`M-Pesa STK push sent to ${sub.group_phone_number}`);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to send M-Pesa reminder');
                              } finally {
                                setProcessing(false);
                              }
                            }}
                            disabled={processing}
                            title="Send M-Pesa Subscription Reminder"
                            data-testid={`mpesa-reminder-btn-${sub.group_id}`}
                          >
                            📱 M-Pesa
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Change Tier Modal */}
      {showTierModal && selectedSubscription && (
        <div className="modal-overlay" onClick={() => setShowTierModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>📊 Change Subscription Tier</h2>
              <button className="close-button" onClick={() => setShowTierModal(false)}>×</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px', padding: '16px', background: '#F8FAFC', borderRadius: '8px' }}>
                <h3 style={{ margin: '0 0 8px 0' }}>{selectedSubscription.group_name}</h3>
                <p style={{ margin: 0, color: '#64748b' }}>
                  Current Tier: <strong>{selectedSubscription.subscription_tier.toUpperCase()}</strong> ({selectedSubscription.tier_name})
                </p>
                <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>
                  Members: {selectedSubscription.member_count}
                </p>
              </div>

              <div className="form-group">
                <label>Select New Tier</label>
                <select
                  value={newTier}
                  onChange={(e) => setNewTier(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                >
                  {Object.entries(TIERS).map(([tier, info]) => (
                    <option key={tier} value={tier}>
                      {tier.toUpperCase()} - {info.name} {info.monthlyPrice !== null ? `($${info.monthlyPrice}/mo)` : '(Custom Pricing)'}
                    </option>
                  ))}
                </select>
              </div>

              {newTier && newTier !== selectedSubscription.subscription_tier && (
                <div style={{ marginTop: '16px', padding: '16px', background: newTier === 'custom' ? '#EDE9FE' : '#FEF3C7', borderRadius: '8px', borderLeft: `4px solid ${newTier === 'custom' ? '#7C3AED' : '#F59E0B'}` }}>
                  <strong>{newTier === 'custom' ? 'Custom Tier:' : 'Tier Change Summary:'}</strong>
                  <p style={{ margin: '8px 0 0 0' }}>
                    {selectedSubscription.subscription_tier.toUpperCase()} → {newTier.toUpperCase()}
                  </p>
                  {newTier === 'custom' ? (
                    <p style={{ margin: '4px 0 0 0', color: '#6B21A8' }}>
                      You will be prompted to set custom pricing after confirming.
                    </p>
                  ) : (
                    <p style={{ margin: '4px 0 0 0' }}>
                      ${selectedSubscription.monthly_price}/mo → ${TIERS[newTier]?.monthlyPrice}/mo
                    </p>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button 
                  className="btn btn-secondary" 
                  style={{ flex: 1 }}
                  onClick={() => setShowTierModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1 }}
                  onClick={handleChangeTier}
                  disabled={processing || newTier === selectedSubscription.subscription_tier}
                >
                  {processing ? 'Changing...' : 'Change Tier'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Pricing Modal */}
      {showCustomPricingModal && selectedSubscription && (
        <div className="modal-overlay" onClick={() => setShowCustomPricingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>💲 Set Custom Pricing</h2>
              <button className="close-button" onClick={() => setShowCustomPricingModal(false)}>×</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px', padding: '16px', background: '#EDE9FE', borderRadius: '8px', borderLeft: '4px solid #7C3AED' }}>
                <h3 style={{ margin: '0 0 8px 0' }}>{selectedSubscription.group_name}</h3>
                <p style={{ margin: 0, color: '#6B21A8' }}>
                  Members: <strong>{selectedSubscription.member_count}</strong>
                </p>
                {selectedSubscription.custom_pricing && (
                  <p style={{ margin: '4px 0 0 0', color: '#6B21A8', fontSize: '13px' }}>
                    Current custom price: ${selectedSubscription.custom_pricing.monthly_price}/mo
                  </p>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontWeight: 600 }}>Monthly Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customPricingForm.monthly_price}
                  onChange={(e) => setCustomPricingForm({...customPricingForm, monthly_price: e.target.value})}
                  placeholder="e.g. 99.99"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  data-testid="custom-monthly-price"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontWeight: 600 }}>Annual Price ($) <span style={{ color: '#9CA3AF', fontWeight: 400 }}>— leave blank for auto 10% discount</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customPricingForm.annual_price}
                  onChange={(e) => setCustomPricingForm({...customPricingForm, annual_price: e.target.value})}
                  placeholder={customPricingForm.monthly_price ? `Auto: $${(customPricingForm.monthly_price * 12 * 0.9).toFixed(2)}` : 'Auto-calculated'}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  data-testid="custom-annual-price"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontWeight: 600 }}>Max Members <span style={{ color: '#9CA3AF', fontWeight: 400 }}>— leave blank for unlimited</span></label>
                <input
                  type="number"
                  min="1501"
                  value={customPricingForm.max_members}
                  onChange={(e) => setCustomPricingForm({...customPricingForm, max_members: e.target.value})}
                  placeholder="Unlimited"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  data-testid="custom-max-members"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontWeight: 600 }}>Notes</label>
                <textarea
                  value={customPricingForm.notes}
                  onChange={(e) => setCustomPricingForm({...customPricingForm, notes: e.target.value})}
                  placeholder="Internal notes about this pricing arrangement..."
                  rows={2}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #E2E8F0', resize: 'vertical' }}
                  data-testid="custom-pricing-notes"
                />
              </div>

              {customPricingForm.monthly_price && (
                <div style={{ padding: '12px', background: '#F0FDF4', borderRadius: '8px', marginBottom: '16px', borderLeft: '4px solid #16A34A' }}>
                  <div style={{ fontSize: '13px', color: '#166534' }}>
                    <strong>Pricing Summary:</strong><br />
                    Monthly: <strong>${parseFloat(customPricingForm.monthly_price).toFixed(2)}/mo</strong><br />
                    Annual: <strong>${customPricingForm.annual_price ? parseFloat(customPricingForm.annual_price).toFixed(2) : (customPricingForm.monthly_price * 12 * 0.9).toFixed(2)}/yr</strong>
                    {!customPricingForm.annual_price && <span style={{ color: '#6B7280' }}> (10% discount)</span>}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  className="btn btn-secondary" 
                  style={{ flex: 1 }}
                  onClick={() => setShowCustomPricingModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1, background: '#7C3AED' }}
                  onClick={handleSetCustomPricing}
                  disabled={processing || !customPricingForm.monthly_price}
                  data-testid="confirm-custom-pricing-btn"
                >
                  {processing ? 'Saving...' : 'Set Custom Pricing'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminSupportTab() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all'); // all, open, in_progress, resolved

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await axios.get(`${API}/support/tickets`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.patch(`${API}/support/tickets/${ticketId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Ticket status updated to ${newStatus}`);
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      });
      toast.success('Reply sent successfully');
      setReplyMessage('');
      loadTickets();
      // Reload the ticket to show new reply
      const response = await axios.get(`${API}/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'status-pending';
      case 'in_progress': return 'status-processing';
      case 'resolved': return 'status-approved';
      case 'closed': return 'status-inactive';
      default: return 'status-pending';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    return ticket.status === filter;
  });

  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  if (loading) {
    return <div>Loading support tickets...</div>;
  }

  return (
    <div className="support-tab" data-testid="admin-support-tab">
      <div className="support-header">
        <h1>🎫 Support Tickets</h1>
        <p className="support-subtitle">Manage support requests from group leadership</p>
      </div>

      {/* Stats Cards */}
      <div className="support-stats-grid">
        <div className="stat-card card" onClick={() => setFilter('all')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">📋</div>
          <div className="stat-value">{ticketStats.total}</div>
          <div className="stat-label">Total Tickets</div>
        </div>
        <div className="stat-card card" onClick={() => setFilter('open')} style={{ cursor: 'pointer', borderLeft: '4px solid #E74C3C' }}>
          <div className="stat-icon">🔴</div>
          <div className="stat-value">{ticketStats.open}</div>
          <div className="stat-label">Open</div>
        </div>
        <div className="stat-card card" onClick={() => setFilter('in_progress')} style={{ cursor: 'pointer', borderLeft: '4px solid #F39C12' }}>
          <div className="stat-icon">🟡</div>
          <div className="stat-value">{ticketStats.in_progress}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card card" onClick={() => setFilter('resolved')} style={{ cursor: 'pointer', borderLeft: '4px solid #27AE60' }}>
          <div className="stat-icon">🟢</div>
          <div className="stat-value">{ticketStats.resolved}</div>
          <div className="stat-label">Resolved</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar card" style={{ display: 'flex', gap: '8px', padding: '12px', marginBottom: '20px' }}>
        <button 
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
        >
          All ({ticketStats.total})
        </button>
        <button 
          className={`btn ${filter === 'open' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('open')}
        >
          Open ({ticketStats.open})
        </button>
        <button 
          className={`btn ${filter === 'in_progress' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('in_progress')}
        >
          In Progress ({ticketStats.in_progress})
        </button>
        <button 
          className={`btn ${filter === 'resolved' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('resolved')}
        >
          Resolved ({ticketStats.resolved})
        </button>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="empty-state card">
          <h3>No support tickets</h3>
          <p>{filter === 'all' ? 'No tickets from leadership yet' : `No ${filter.replace('_', ' ')} tickets`}</p>
        </div>
      ) : (
        <div className="tickets-list">
          {filteredTickets.map((ticket) => (
            <div 
              key={ticket.id} 
              className={`ticket-card card ${selectedTicket?.id === ticket.id ? 'selected' : ''}`}
              onClick={() => setSelectedTicket(ticket)}
              style={{ cursor: 'pointer', marginBottom: '12px' }}
              data-testid={`ticket-${ticket.id}`}
            >
              <div className="ticket-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ marginBottom: '4px' }}>{ticket.subject}</h3>
                  <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>
                    From: <strong>{ticket.user_name}</strong> ({ticket.user_email})
                    {ticket.group_name && <span> • Group: <strong>{ticket.group_name}</strong></span>}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <span className={`status-badge ${getPriorityBadgeClass(ticket.priority)}`} style={{ textTransform: 'uppercase', fontSize: '10px' }}>
                    {ticket.priority}
                  </span>
                  <span className={`status-badge ${getStatusBadgeClass(ticket.status)}`}>
                    {ticket.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              
              <div className="ticket-preview" style={{ marginBottom: '12px' }}>
                <p style={{ color: '#374151', fontSize: '14px' }}>
                  {ticket.message.length > 150 ? ticket.message.substring(0, 150) + '...' : ticket.message}
                </p>
              </div>
              
              <div className="ticket-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#64748b' }}>
                <span>Category: {ticket.category}</span>
                <span>{new Date(ticket.created_at).toLocaleString()}</span>
                {ticket.replies && ticket.replies.length > 0 && (
                  <span>💬 {ticket.replies.length} replies</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>🎫 Ticket Details</h2>
              <button className="close-button" onClick={() => setSelectedTicket(null)}>×</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {/* Ticket Info */}
              <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '12px' }}>{selectedTicket.subject}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                  <div>
                    <strong>From:</strong> {selectedTicket.user_name}
                    <br />
                    <span style={{ color: '#64748b' }}>{selectedTicket.user_email}</span>
                  </div>
                  <div>
                    <strong>Group:</strong> {selectedTicket.group_name || 'N/A'}
                  </div>
                  <div>
                    <strong>Category:</strong> {selectedTicket.category}
                  </div>
                  <div>
                    <strong>Priority:</strong> 
                    <span className={`status-badge ${getPriorityBadgeClass(selectedTicket.priority)}`} style={{ marginLeft: '8px' }}>
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <div>
                    <strong>Created:</strong> {new Date(selectedTicket.created_at).toLocaleString()}
                  </div>
                  <div>
                    <strong>Status:</strong>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                      style={{ marginLeft: '8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Original Message */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '8px' }}>Original Message</h4>
                <div style={{ padding: '16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.message}</p>
                </div>
              </div>

              {/* Replies */}
              {selectedTicket.replies && selectedTicket.replies.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '12px' }}>Conversation ({selectedTicket.replies.length})</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selectedTicket.replies.map((reply, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          padding: '12px', 
                          background: reply.user_role === 'super_admin' ? '#EBF5FF' : '#F0FDF4',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${reply.user_role === 'super_admin' ? '#3B82F6' : '#22C55E'}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
                          <strong style={{ color: reply.user_role === 'super_admin' ? '#3B82F6' : '#22C55E' }}>
                            {reply.user_name} ({reply.user_role === 'super_admin' ? 'Admin' : 'Leadership'})
                          </strong>
                          <span>{new Date(reply.created_at).toLocaleString()}</span>
                        </div>
                        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{reply.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply Form */}
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '8px' }}>Send Reply</h4>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your response here..."
                  rows={4}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '12px', resize: 'vertical' }}
                  data-testid="reply-textarea"
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setSelectedTicket(null)}
                  >
                    Close
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleReply}
                    disabled={sending || !replyMessage.trim()}
                    data-testid="send-reply-btn"
                  >
                    {sending ? 'Sending...' : '📤 Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState({
    platform_name: 'ChamaLink',
    platform_logo_url: '',
    platform_logo_text: '💰',
    transaction_fee_rate: 4.0,
    parsing_fee_rate: 1.0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      setPreviewUrl(response.data.platform_logo_url || '');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Platform settings updated successfully');
      // Reload the page to show new logo everywhere
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUrlChange = (url) => {
    setSettings({ ...settings, platform_logo_url: url });
    setPreviewUrl(url);
  };

  if (loading) {
    return <div>Loading settings...</div>;
  }

  return (
    <div className="settings-tab">
      <h1>⚙️ Platform Settings</h1>

      <div className="settings-section card">
        <h2>Branding</h2>
        <p className="section-description">Customize your platform's appearance and branding</p>

        <div className="form-group">
          <label>Platform Name</label>
          <input
            type="text"
            value={settings.platform_name}
            onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
            className="form-control"
            placeholder="ChamaLink"
          />
          <small className="form-hint">This name appears throughout the platform</small>
        </div>

        <div className="form-group">
          <label>Logo URL</label>
          <input
            type="url"
            value={settings.platform_logo_url || ''}
            onChange={(e) => handleLogoUrlChange(e.target.value)}
            className="form-control"
            placeholder="https://example.com/logo.png"
          />
          <small className="form-hint">Enter the URL of your logo image (PNG, JPG, or SVG)</small>
        </div>

        <div className="form-group">
          <label>Logo Text/Emoji Fallback</label>
          <input
            type="text"
            value={settings.platform_logo_text}
            onChange={(e) => setSettings({ ...settings, platform_logo_text: e.target.value })}
            className="form-control"
            placeholder="💰"
            maxLength="2"
          />
          <small className="form-hint">Displayed when logo URL is not provided (emoji or 1-2 characters)</small>
        </div>

        <div className="logo-preview-section">
          <h3>Logo Preview</h3>
          <div className="logo-preview-container">
            {previewUrl ? (
              <img 
                src={previewUrl} 
                alt="Platform Logo" 
                className="logo-preview-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  toast.error('Invalid logo URL - please check the image URL');
                }}
              />
            ) : (
              <div className="logo-preview-fallback">
                <span className="logo-preview-text">{settings.platform_logo_text}</span>
              </div>
            )}
            <p className="platform-name-preview">{settings.platform_name}</p>
          </div>
        </div>

        <div className="settings-info card">
          <h4>📝 Logo Guidelines:</h4>
          <ul>
            <li>Use a square or horizontal logo for best results</li>
            <li>Recommended size: 200x200px or larger</li>
            <li>Supported formats: PNG (with transparency), JPG, SVG</li>
            <li>Host your logo on a reliable image hosting service</li>
            <li>Make sure the URL is publicly accessible</li>
          </ul>
        </div>

        <div className="settings-actions">
          <button 
            className="btn btn-secondary"
            onClick={loadSettings}
            disabled={saving}
          >
            Reset Changes
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Fee Configuration Section */}
      <div className="settings-section card" data-testid="fee-configuration-section">
        <h2>Fee Configuration</h2>
        <p className="section-description">Configure platform transaction and Plaid parsing fees. Set to 0 to waive the fee entirely.</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Transaction Fee (%)</label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.transaction_fee_rate}
                onChange={(e) => setSettings({ ...settings, transaction_fee_rate: parseFloat(e.target.value) || 0 })}
                className="form-control"
                data-testid="transaction-fee-input"
                style={{ paddingRight: '36px' }}
              />
              <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontWeight: '600' }}>%</span>
            </div>
            <small className="form-hint">Applied to all member payment transactions via Stripe (default: 4%)</small>
            <div style={{
              marginTop: '8px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
              background: settings.transaction_fee_rate === 0 ? '#F0FDF4' : '#F8FAFC',
              border: `1px solid ${settings.transaction_fee_rate === 0 ? '#BBF7D0' : '#E2E8F0'}`,
              color: settings.transaction_fee_rate === 0 ? '#166534' : '#475569'
            }}>
              {settings.transaction_fee_rate === 0
                ? 'Transaction fee is waived (0%)'
                : `Members pay ${settings.transaction_fee_rate}% extra on each contribution`}
            </div>
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label>Plaid Parsing Fee (%)</label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.parsing_fee_rate}
                onChange={(e) => setSettings({ ...settings, parsing_fee_rate: parseFloat(e.target.value) || 0 })}
                className="form-control"
                data-testid="parsing-fee-input"
                style={{ paddingRight: '36px' }}
              />
              <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontWeight: '600' }}>%</span>
            </div>
            <small className="form-hint">Charged on each confirmed Plaid bank transaction match (default: 1%)</small>
            <div style={{
              marginTop: '8px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
              background: settings.parsing_fee_rate === 0 ? '#F0FDF4' : '#F8FAFC',
              border: `1px solid ${settings.parsing_fee_rate === 0 ? '#BBF7D0' : '#E2E8F0'}`,
              color: settings.parsing_fee_rate === 0 ? '#166534' : '#475569'
            }}>
              {settings.parsing_fee_rate === 0
                ? 'Parsing fee is waived (0%)'
                : `Groups are charged ${settings.parsing_fee_rate}% per confirmed bank transaction`}
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button 
            className="btn btn-secondary"
            onClick={loadSettings}
            disabled={saving}
          >
            Reset
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-fee-settings-btn"
          >
            {saving ? 'Saving...' : 'Save Fee Settings'}
          </button>
        </div>
      </div>

      <div className="settings-section card">
        <h2>Popular Image Hosting Services</h2>
        <p className="section-description">Upload your logo to one of these services and use the public URL:</p>
        <div className="hosting-services">
          <div className="service-item">
            <strong>Imgur:</strong> <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer">imgur.com/upload</a>
            <p>Free, no account required</p>
          </div>
          <div className="service-item">
            <strong>Cloudinary:</strong> <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer">cloudinary.com</a>
            <p>Free tier available, image optimization</p>
          </div>
          <div className="service-item">
            <strong>ImgBB:</strong> <a href="https://imgbb.com" target="_blank" rel="noopener noreferrer">imgbb.com</a>
            <p>Free hosting with direct links</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginActivityTab() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', status: '', search: '', start_date: '', end_date: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTile, setActiveTile] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = async (f = filters) => {
    try {
      const params = {};
      if (f.role) params.role = f.role;
      if (f.search) params.search = f.search;
      if (f.start_date) params.start_date = f.start_date;
      if (f.end_date) params.end_date = f.end_date;
      const res = await axios.get(`${API}/admin/login-activity/stats`, { headers, params });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  };

  const fetchLogs = async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      if (f.role) params.role = f.role;
      if (f.status) params.status = f.status;
      if (f.search) params.search = f.search;
      if (f.start_date) params.start_date = f.start_date;
      if (f.end_date) params.end_date = f.end_date;
      const res = await axios.get(`${API}/admin/login-activity`, { headers, params });
      setLogs(res.data.logs);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load login activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); fetchLogs(1); }, []);

  const applyFilters = () => { setPage(1); fetchStats(filters); fetchLogs(1, filters); };
  const clearFilters = () => {
    const cleared = { role: '', status: '', search: '', start_date: '', end_date: '' };
    setFilters(cleared);
    setActiveTile(null);
    setPage(1);
    fetchStats(cleared);
    fetchLogs(1, cleared);
  };

  const handleTileClick = (statusKey) => {
    if (activeTile === statusKey) {
      const newFilters = { ...filters, status: '' };
      setFilters(newFilters);
      setActiveTile(null);
      setPage(1);
      fetchLogs(1, newFilters);
    } else {
      const newFilters = { ...filters, status: statusKey };
      setFilters(newFilters);
      setActiveTile(statusKey);
      setPage(1);
      fetchLogs(1, newFilters);
    }
  };

  const goToPage = (p) => { setPage(p); fetchLogs(p, filters); };

  const statusBadge = (status) => {
    const map = {
      success: { bg: '#DEF7EC', color: '#03543F', label: 'Success' },
      failed: { bg: '#FDE8E8', color: '#9B1C1C', label: 'Failed' },
      '2fa_sent': { bg: '#E1EFFE', color: '#1E429F', label: '2FA Sent' },
      '2fa_failed': { bg: '#FDE8E8', color: '#9B1C1C', label: '2FA Failed' },
      blocked: { bg: '#FEF3C7', color: '#92400E', label: 'Blocked' }
    };
    const s = map[status] || { bg: '#F3F4F6', color: '#374151', label: status };
    return (
      <span data-testid={`status-badge-${status}`} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>
    );
  };

  const roleBadge = (role) => {
    const map = {
      super_admin: { bg: '#EDE9FE', color: '#5B21B6' },
      leadership: { bg: '#DBEAFE', color: '#1E40AF' },
      member: { bg: '#D1FAE5', color: '#065F46' },
      limited_admin: { bg: '#FEF3C7', color: '#92400E' },
      unknown: { bg: '#F3F4F6', color: '#6B7280' }
    };
    const r = map[role] || map['unknown'];
    return (
      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: r.bg, color: r.color }}>{role?.replace('_', ' ')}</span>
    );
  };

  const hasActiveFilters = filters.role || filters.status || filters.search || filters.start_date || filters.end_date;

  const tiles = [
    { key: 'today', label: 'Today', value: stats?.today_total ?? 0, sub: stats?.today_failed > 0 ? `${stats.today_failed} failed` : null, subColor: '#DC2626', bg: '#F9FAFB', borderColor: '#E5E7EB', accent: '#1F2937', statusFilter: null },
    { key: 'success', label: 'Successful', value: stats?.successful ?? 0, sub: null, subColor: null, bg: '#F0FDF4', borderColor: '#BBF7D0', accent: '#16A34A', statusFilter: 'success' },
    { key: 'failed', label: 'Failed', value: stats?.failed ?? 0, sub: null, subColor: null, bg: '#FEF2F2', borderColor: '#FECACA', accent: '#DC2626', statusFilter: 'failed' },
    { key: '2fa_sent', label: '2FA Sent', value: stats?.two_fa_sent ?? 0, sub: null, subColor: null, bg: '#EFF6FF', borderColor: '#BFDBFE', accent: '#2563EB', statusFilter: '2fa_sent' },
    { key: 'blocked', label: 'Blocked', value: stats?.blocked ?? 0, sub: null, subColor: null, bg: '#FEF3C7', borderColor: '#FDE68A', accent: '#D97706', statusFilter: 'blocked' },
  ];

  return (
    <div data-testid="login-activity-tab" style={{ padding: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', color: '#1F2937' }}>Login Activity</h2>
        <button onClick={() => { fetchStats(filters); fetchLogs(page, filters); }} className="btn btn-secondary" data-testid="refresh-activity-btn" style={{ fontSize: '13px', padding: '6px 14px' }}>
          Refresh
        </button>
      </div>

      {stats && (
        <div data-testid="activity-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
          <style>{`
            @media (max-width: 900px) { [data-testid="activity-stats"] { grid-template-columns: repeat(3, 1fr) !important; } }
            @media (max-width: 600px) { [data-testid="activity-stats"] { grid-template-columns: repeat(2, 1fr) !important; } }
          `}</style>
          {tiles.map(tile => {
            const isActive = tile.statusFilter && activeTile === tile.statusFilter;
            const isClickable = !!tile.statusFilter;
            return (
              <div
                key={tile.key}
                data-testid={`tile-${tile.key}`}
                onClick={() => isClickable && handleTileClick(tile.statusFilter)}
                style={{
                  background: isActive ? tile.accent : tile.bg,
                  borderRadius: '10px', padding: '16px',
                  border: isActive ? `2px solid ${tile.accent}` : `1px solid ${tile.borderColor}`,
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  transform: isActive ? 'translateY(-2px)' : 'none',
                  boxShadow: isActive ? `0 6px 16px ${tile.accent}33` : 'none',
                  position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={e => { if (isClickable && !isActive) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${tile.borderColor}`; } }}
                onMouseLeave={e => { if (isClickable && !isActive) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; } }}
              >
                <div style={{ fontSize: '12px', color: isActive ? 'rgba(255,255,255,0.8)' : '#6B7280', marginBottom: '4px', fontWeight: 500 }}>
                  {tile.label}
                  {isClickable && <span style={{ float: 'right', fontSize: '10px', opacity: 0.6 }}>{isActive ? 'ACTIVE' : 'CLICK'}</span>}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: isActive ? '#FFFFFF' : tile.accent, fontVariantNumeric: 'tabular-nums' }}>{tile.value}</div>
                {tile.sub && <div style={{ fontSize: '11px', color: isActive ? 'rgba(255,255,255,0.7)' : tile.subColor, marginTop: '2px' }}>{tile.sub}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div data-testid="activity-filters" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Search email</label>
          <input type="text" placeholder="Search email..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} data-testid="filter-search" style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', width: '180px' }} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Role</label>
          <select value={filters.role} onChange={e => setFilters({...filters, role: e.target.value})} data-testid="filter-role" style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}>
            <option value="">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="leadership">Leadership</option>
            <option value="member">Member</option>
            <option value="limited_admin">Limited Admin</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Status</label>
          <select value={filters.status} onChange={e => { setFilters({...filters, status: e.target.value}); setActiveTile(e.target.value || null); }} data-testid="filter-status" style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}>
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="2fa_sent">2FA Sent</option>
            <option value="2fa_failed">2FA Failed</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>From</label>
          <input type="date" value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} data-testid="filter-start-date" style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>To</label>
          <input type="date" value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} data-testid="filter-end-date" style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <button onClick={applyFilters} className="btn btn-primary" data-testid="apply-filters-btn" style={{ padding: '7px 16px', fontSize: '13px' }}>Apply</button>
        {hasActiveFilters && <button onClick={clearFilters} className="btn btn-secondary" data-testid="clear-filters-btn" style={{ padding: '7px 16px', fontSize: '13px' }}>Clear</button>}
      </div>

      <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '10px' }}>{total} record{total !== 1 ? 's' : ''} found</div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No login activity found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="activity-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Email</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Role</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>IP Address</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Date & Time</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id || i} style={{ borderBottom: '1px solid #E5E7EB' }} data-testid={`activity-row-${i}`}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>{log.email}</td>
                  <td style={{ padding: '10px 12px', color: '#6B7280' }}>{log.name || '-'}</td>
                  <td style={{ padding: '10px 12px' }}>{roleBadge(log.role)}</td>
                  <td style={{ padding: '10px 12px' }}>{statusBadge(log.status)}</td>
                  <td style={{ padding: '10px 12px', color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>{log.ip_address || '-'}</td>
                  <td style={{ padding: '10px 12px', color: '#6B7280' }}>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                  <td style={{ padding: '10px 12px', color: '#9CA3AF', fontSize: '12px' }}>{log.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div data-testid="activity-pagination" style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px', alignItems: 'center' }}>
          <button disabled={page <= 1} onClick={() => goToPage(page - 1)} style={{ padding: '6px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', background: page <= 1 ? '#F3F4F6' : 'white', cursor: page <= 1 ? 'default' : 'pointer', fontSize: '13px' }}>Prev</button>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => goToPage(page + 1)} style={{ padding: '6px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', background: page >= totalPages ? '#F3F4F6' : 'white', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: '13px' }}>Next</button>
        </div>
      )}
    </div>
  );
}